import { useCallback, useEffect, useRef } from 'react';

import { useConnectivity } from '../contexts/ConnectivityContext';
import {
    resetPlayerDiagnosticsForMedia,
    setPlayerDiagnosticsBrowserMedia,
    setPlayerDiagnosticsCommand,
    setPlayerDiagnosticsPlayback,
    setPlayerDiagnosticsPlayPauseCommand,
    setPlayerDiagnosticsSeek,
} from '../stories/Store/playerSlice';
import { CustomToastType } from '../stories/Store/toastSlice';
import {
    createInitialNativeMediaErrorDiagnostics,
    createInitialTimeRangeSnapshot,
    didSeekReachTarget,
    getLikelySeekFailureReason,
    PlayerCommandDiagnostics,
    PlayerVideoSourceType,
    SEEK_TIMEOUT,
    SeekRequestSource,
} from '../utility/playerDiagnostics';
import { useAppDispatch, useAppSelector } from '.';
import { useToasts } from './useToasts';

interface UsePlayerDiagnosticsProps {
    currentMediaFile: string;
    currentPlaylistStartTime: number;
    playerRef: React.MutableRefObject<DiagnosticsPlayerRef | null>;
    videoSourceType: PlayerVideoSourceType;
    videoElementRef?: React.MutableRefObject<HTMLVideoElement | undefined>;
}

interface DiagnosticsPlayerRef {
    getPosition?: () => number;
    getDuration?: () => number;
}

interface DiagnosticsTimeEvent {
    duration: number;
    position: number;
}

interface DiagnosticsSeekEvent {
    offset: number;
    position?: number;
}

interface DiagnosticsPlayerEvent {
    code?: number;
    message?: string;
    pauseReason?: string;
    type?: string;
}

export const usePlayerDiagnostics = ({
    currentMediaFile,
    currentPlaylistStartTime,
    playerRef,
    videoSourceType,
    videoElementRef,
}: UsePlayerDiagnosticsProps) => {
    const dispatch = useAppDispatch();
    const isPlayerDiagnosticsActive = useAppSelector((state) => state.player.isPlayerDiagnosticsActive);
    const rangeProbe = useAppSelector((state) => state.player.diagnostics.rangeProbe);
    const playbackState = useAppSelector((state) => state.player.diagnostics.playbackState);

    const { checkInternetReachability } = useConnectivity();

    const { showAlertToast, clearToasts } = useToasts();

    const pendingSeekRef = useRef<{
        source: SeekRequestSource;
        targetPosition: number;
        startingPosition: number | null;
        requestedAt: string;
    } | null>(null);
    const pendingCommandRef = useRef<{
        command: 'play' | 'pause' | 'seek';
        requestedAt: string;
        details: string | null;
    } | null>(null);
    const rangeProbeRef = useRef(rangeProbe);
    const playbackStateRef = useRef(playbackState);
    const seekFailureTimeoutRef = useRef<number>();

    useEffect(() => {
        rangeProbeRef.current = rangeProbe;
    }, [rangeProbe]);

    useEffect(() => {
        playbackStateRef.current = playbackState;
    }, [playbackState]);

    const publishPlaybackDiagnostics = useCallback((
        payload: Parameters<typeof setPlayerDiagnosticsPlayback>[0],
        options?: { skipWhenHidden?: boolean }
    ) => {
        if (options?.skipWhenHidden && !isPlayerDiagnosticsActive) {
            return;
        }

        dispatch(setPlayerDiagnosticsPlayback(payload));
    }, [dispatch, isPlayerDiagnosticsActive]);

    const getTimeRangeSnapshot = useCallback((timeRanges?: TimeRanges | null) => {
        if (!timeRanges || timeRanges.length === 0) {
            return createInitialTimeRangeSnapshot();
        }

        return {
            start: timeRanges.start(0),
            end: timeRanges.end(timeRanges.length - 1),
            length: timeRanges.length,
        };
    }, []);

    const syncBrowserMediaDiagnostics = useCallback((
        videoElement?: HTMLVideoElement | null,
        options?: { skipWhenHidden?: boolean }
    ) => {
        if (options?.skipWhenHidden && !isPlayerDiagnosticsActive) {
            return;
        }

        const element = videoElement ?? videoElementRef?.current;

        if (!element) {
            return;
        }

        const nativeError = element.error
            ? {
                code: element.error.code ?? null,
                message: element.error.message ?? null,
                capturedAt: new Date().toISOString(),
            }
            : createInitialNativeMediaErrorDiagnostics();

        dispatch(setPlayerDiagnosticsBrowserMedia({
            currentSrc: element.currentSrc || null,
            readyState: element.readyState ?? null,
            networkState: element.networkState ?? null,
            paused: element.paused ?? null,
            ended: element.ended ?? null,
            seekable: getTimeRangeSnapshot(element.seekable),
            buffered: getTimeRangeSnapshot(element.buffered),
            nativeError,
            lastUpdatedAt: new Date().toISOString(),
        }));
    }, [dispatch, getTimeRangeSnapshot, isPlayerDiagnosticsActive, videoElementRef]);

    const clearPendingSeekTimeout = useCallback(() => {
        if (seekFailureTimeoutRef.current) {
            clearTimeout(seekFailureTimeoutRef.current);
            seekFailureTimeoutRef.current = undefined;
        }
    }, []);

    const requestCommand = useCallback((command: 'play' | 'pause' | 'seek', details: string | null = null) => {
        const requestedAt = new Date().toISOString();
        const payload: Partial<PlayerCommandDiagnostics> = {
            command,
            status: 'requested',
            requestedAt,
            resolvedAt: null,
            details,
        };

        pendingCommandRef.current = {
            command,
            requestedAt,
            details,
        };

        dispatch(setPlayerDiagnosticsCommand(payload));

        if (command === 'play' || command === 'pause') {
            dispatch(setPlayerDiagnosticsPlayPauseCommand(payload));
        }
    }, [dispatch]);

    const resolvePendingCommand = useCallback((command: 'play' | 'pause' | 'seek', details: string | null = null) => {
        if (pendingCommandRef.current?.command !== command) {
            return;
        }

        const payload: Partial<PlayerCommandDiagnostics> = {
            status: 'succeeded',
            resolvedAt: new Date().toISOString(),
            details: details ?? pendingCommandRef.current.details,
        };

        dispatch(setPlayerDiagnosticsCommand(payload));

        if (command === 'play' || command === 'pause') {
            dispatch(setPlayerDiagnosticsPlayPauseCommand(payload));
        }

        pendingCommandRef.current = null;
    }, [dispatch]);

    const failPendingCommand = useCallback((command: 'play' | 'pause' | 'seek', details: string) => {
        if (pendingCommandRef.current?.command !== command) {
            return;
        }

        const payload: Partial<PlayerCommandDiagnostics> = {
            status: 'failed',
            resolvedAt: new Date().toISOString(),
            details,
        };

        dispatch(setPlayerDiagnosticsCommand(payload));

        if (command === 'play' || command === 'pause') {
            dispatch(setPlayerDiagnosticsPlayPauseCommand(payload));
        }

        pendingCommandRef.current = null;
    }, [dispatch]);

    const failPendingSeek = useCallback((observedPosition: number, failureReasonOverride?: string) => {
        const pendingSeek = pendingSeekRef.current;

        if (!pendingSeek) {
            return;
        }

        const failureReason = failureReasonOverride ?? getLikelySeekFailureReason({
            rangeProbe: rangeProbeRef.current,
            targetPosition: pendingSeek.targetPosition,
            observedPosition,
        });

        clearPendingSeekTimeout();
        pendingSeekRef.current = null;

        dispatch(setPlayerDiagnosticsSeek({
            status: 'failed',
            source: pendingSeek.source,
            targetPosition: pendingSeek.targetPosition,
            startingPosition: pendingSeek.startingPosition,
            requestedAt: pendingSeek.requestedAt,
            resolvedAt: new Date().toISOString(),
            observedPosition,
            failureReason,
        }));

        failPendingCommand('seek', failureReason);

        checkInternetReachability()
            .then(isConnected => {
                // only show seek failure toast in case user is connected
                if (isConnected)
                    showAlertToast({ custom: CustomToastType.SeekFailed });
            });
    }, [checkInternetReachability, clearPendingSeekTimeout, dispatch, failPendingCommand, showAlertToast]);

    const resolvePendingSeek = useCallback((observedPosition: number) => {
        const pendingSeek = pendingSeekRef.current;

        if (!pendingSeek) {
            return false;
        }

        if (playbackStateRef.current === 'error') {
            failPendingSeek(
                observedPosition,
                'JW Player reported a playback error before the seek could be confirmed.'
            );
            return false;
        }

        if (playbackStateRef.current === 'buffering') {
            return false;
        }

        clearPendingSeekTimeout();
        pendingSeekRef.current = null;

        dispatch(setPlayerDiagnosticsSeek({
            status: 'succeeded',
            source: pendingSeek.source,
            targetPosition: pendingSeek.targetPosition,
            startingPosition: pendingSeek.startingPosition,
            requestedAt: pendingSeek.requestedAt,
            resolvedAt: new Date().toISOString(),
            observedPosition,
            failureReason: null,
        }));

        resolvePendingCommand('seek', `Reached ${Math.round(observedPosition)}s.`);
        clearToasts({ customType: CustomToastType.SeekFailed });
        return true;
    }, [clearPendingSeekTimeout, clearToasts, dispatch, failPendingSeek, resolvePendingCommand]);

    const schedulePendingSeekTimeout = useCallback(() => {
        seekFailureTimeoutRef.current = setTimeout(() => {
            const currentPosition = playerRef.current?.getPosition?.() ?? 0;
            const pendingSeekState = pendingSeekRef.current;

            if (!pendingSeekState) {
                return;
            }

            if (didSeekReachTarget({
                targetPosition: pendingSeekState.targetPosition,
                observedPosition: currentPosition,
                startingPosition: pendingSeekState.startingPosition,
            })) {
                const didResolve = resolvePendingSeek(currentPosition);

                if (!didResolve && pendingSeekRef.current) {
                    failPendingSeek(
                        currentPosition,
                        'The player reached the requested position but remained buffering instead of becoming ready to play.'
                    );
                }

                return;
            }

            failPendingSeek(currentPosition);
        }, SEEK_TIMEOUT) as unknown as number;
    }, [failPendingSeek, playerRef, resolvePendingSeek]);

    const registerSeekIntent = useCallback((
        targetPosition: number,
        source: SeekRequestSource,
        startingPosition: number | null
    ) => {
        const requestedAt = new Date().toISOString();
        const resolvedStartingPosition = startingPosition ?? 0;

        clearPendingSeekTimeout();

        pendingSeekRef.current = {
            source,
            targetPosition,
            startingPosition: resolvedStartingPosition,
            requestedAt,
        };

        dispatch(setPlayerDiagnosticsSeek({
            status: 'pending',
            source,
            targetPosition,
            startingPosition: resolvedStartingPosition,
            requestedAt,
            resolvedAt: null,
            observedPosition: null,
            failureReason: null,
        }));

        schedulePendingSeekTimeout();
    }, [clearPendingSeekTimeout, dispatch, schedulePendingSeekTimeout]);

    useEffect(() => {
        return () => {
            clearPendingSeekTimeout();
        };
    }, [clearPendingSeekTimeout]);

    useEffect(() => {
        if (!currentMediaFile && videoSourceType === null) {
            return;
        }

        clearPendingSeekTimeout();
        pendingSeekRef.current = null;
        pendingCommandRef.current = null;

        dispatch(resetPlayerDiagnosticsForMedia({
            currentFile: currentMediaFile,
            sourceType: videoSourceType,
        }));

        if (currentPlaylistStartTime > 0) {
            registerSeekIntent(currentPlaylistStartTime, 'initial-starttime', 0);
        }
    }, [
        clearPendingSeekTimeout,
        currentMediaFile,
        currentPlaylistStartTime,
        dispatch,
        registerSeekIntent,
        videoSourceType,
    ]);

    useEffect(() => {
        if (!isPlayerDiagnosticsActive) {
            return;
        }

        publishPlaybackDiagnostics({
            currentFile: currentMediaFile,
            currentPosition: playerRef.current?.getPosition?.() ?? 0,
            currentDuration: playerRef.current?.getDuration?.() ?? 0,
            playbackState: playbackStateRef.current,
        });
        syncBrowserMediaDiagnostics();
    }, [
        currentMediaFile,
        isPlayerDiagnosticsActive,
        playerRef,
        publishPlaybackDiagnostics,
        syncBrowserMediaDiagnostics,
    ]);

    const handleReadyDiagnostics = useCallback(() => {
        playbackStateRef.current = 'idle';
        publishPlaybackDiagnostics({
            currentFile: currentMediaFile,
            playbackState: 'idle',
        });
        syncBrowserMediaDiagnostics();
    }, [currentMediaFile, publishPlaybackDiagnostics, syncBrowserMediaDiagnostics]);

    const handlePlayDiagnostics = useCallback(() => {
        playbackStateRef.current = 'playing';
        publishPlaybackDiagnostics({
            playbackState: 'playing',
        });

        resolvePendingCommand('play', 'Playback started.');
        syncBrowserMediaDiagnostics();

        const pendingSeek = pendingSeekRef.current;
        const currentPosition = playerRef.current?.getPosition?.();

        if (
            pendingSeek &&
            currentPosition !== undefined &&
            didSeekReachTarget({
                targetPosition: pendingSeek.targetPosition,
                observedPosition: currentPosition,
                startingPosition: pendingSeek.startingPosition,
            })
        ) {
            resolvePendingSeek(currentPosition);
        }
    }, [playerRef, publishPlaybackDiagnostics, resolvePendingCommand, resolvePendingSeek, syncBrowserMediaDiagnostics]);

    const handleTimeDiagnostics = useCallback((event: DiagnosticsTimeEvent) => {
        publishPlaybackDiagnostics({
            currentPosition: event.position,
            currentDuration: event.duration,
        }, { skipWhenHidden: true });
        syncBrowserMediaDiagnostics(undefined, { skipWhenHidden: true });

        const pendingSeek = pendingSeekRef.current;
        if (pendingSeek && didSeekReachTarget({
            targetPosition: pendingSeek.targetPosition,
            observedPosition: event.position,
            startingPosition: pendingSeek.startingPosition,
        })) {
            resolvePendingSeek(event.position);
        }
    }, [publishPlaybackDiagnostics, resolvePendingSeek, syncBrowserMediaDiagnostics]);

    const handleSeekDiagnostics = useCallback((event: DiagnosticsSeekEvent) => {
        const pendingSeek = pendingSeekRef.current;
        const isExistingProgrammaticSeek =
            pendingSeek &&
            pendingSeek.source !== 'jw-ui' &&
            Math.abs(pendingSeek.targetPosition - event.offset) < 1;

        if (!isExistingProgrammaticSeek) {
            registerSeekIntent(
                event.offset,
                'jw-ui',
                event.position ?? playerRef.current?.getPosition?.() ?? 0
            );
        }
        syncBrowserMediaDiagnostics();
    }, [playerRef, registerSeekIntent, syncBrowserMediaDiagnostics]);

    const handleSeekedDiagnostics = useCallback(() => {
        const pendingSeek = pendingSeekRef.current;
        const observedPosition = playerRef.current?.getPosition?.() ?? 0;

        if (!pendingSeek) {
            return;
        }

        if (didSeekReachTarget({
            targetPosition: pendingSeek.targetPosition,
            observedPosition,
            startingPosition: pendingSeek.startingPosition,
        })) {
            resolvePendingSeek(observedPosition);
        }
        syncBrowserMediaDiagnostics();
    }, [playerRef, resolvePendingSeek, syncBrowserMediaDiagnostics]);

    const handleErrorDiagnostics = useCallback((event: DiagnosticsPlayerEvent) => {
        playbackStateRef.current = 'error';
        dispatch(setPlayerDiagnosticsPlayback({
            playbackState: 'error',
        }));

        const errorDetails = `JW Player error ${event.code ?? 'unknown'}: ${event.message ?? 'Playback failed.'}`;
        failPendingSeek(playerRef.current?.getPosition?.() ?? 0, errorDetails);
        failPendingCommand('play', errorDetails);
        failPendingCommand('pause', errorDetails);
        syncBrowserMediaDiagnostics();
    }, [dispatch, failPendingCommand, failPendingSeek, playerRef, syncBrowserMediaDiagnostics]);

    const handlePlaybackStoppedDiagnostics = useCallback((event: DiagnosticsPlayerEvent) => {
        const nextPlaybackState = event.type === 'buffer'
            ? 'buffering'
            : event.type === 'complete'
                ? 'complete'
                : event.type === 'error'
                    ? 'error'
                    : event.type === 'pause'
                        ? 'paused'
                        : 'idle';

        playbackStateRef.current = nextPlaybackState;
        publishPlaybackDiagnostics({
            playbackState: nextPlaybackState,
        });

        if (event.type === 'pause') {
            resolvePendingCommand('pause', 'Playback paused.');
        }
        syncBrowserMediaDiagnostics();
    }, [publishPlaybackDiagnostics, resolvePendingCommand, syncBrowserMediaDiagnostics]);

    return {
        handleErrorDiagnostics,
        handlePlaybackStoppedDiagnostics,
        handlePlayDiagnostics,
        handleReadyDiagnostics,
        handleSeekDiagnostics,
        handleSeekedDiagnostics,
        handleTimeDiagnostics,
        registerSeekIntent,
        requestCommand,
        syncBrowserMediaDiagnostics,
    };
};

export default usePlayerDiagnostics;
